import { CronoResponse } from '../../types/response';

/**
 * Single source of truth for the FindNew (internal DB) endpoint paths.
 *
 * All consumers use these builders:
 *  - the extension background `FindNewService` (direct `fetch` + `CookiesProvider` auth)
 *  - the extension `CronoApi` read-through IPC methods (`fetchWithTimeout` + session auth)
 *  - the web app's `useFindNew*` hooks (axios `client`)
 *
 * Builders return a path without a leading slash relative to the API root, matching the app's
 * `config/endpoints.ts` convention and the extension's own transports. Every
 * consumer joins as `${base}/${path}`.
 */
export const findNewPaths = {
  contact: (publicId: string, opts?: { withOtherFields?: boolean }): string =>
    `FindNew/Contacts/${encodeURIComponent(publicId)}?withOtherFields=${
      opts?.withOtherFields ?? true
    }`,
  companyByPublicId: (publicId: string): string =>
    `FindNew/Companies/${encodeURIComponent(publicId)}`,
  companyByNumericId: (numericId: string | number): string =>
    `FindNew/Companies/ByNumericId/${encodeURIComponent(String(numericId))}`,
  companyResolve: (params: {
    linkedinPublicId?: string;
    linkedinNumericId?: string;
  }): string => {
    const search = new URLSearchParams();
    if (params.linkedinNumericId) {
      search.set('linkedinNumericId', params.linkedinNumericId);
    }
    if (params.linkedinPublicId) {
      search.set('linkedinPublicId', params.linkedinPublicId);
    }
    return `FindNew/Companies/Resolve?${search.toString()}`;
  },
};

/**
 * Parses a FindNew fetch `Response` into its data payload, or `null` on a
 * miss (404 / empty data). Callers treat `null` as "not in the DB" and fall
 * through to LinkedIn.
 *
 * Note: this does not catch transport errors — the caller is responsible for
 * wrapping the fetch + parse in a try/catch that also returns `null`.
 */
export async function parseFindNewResponse<T>(
  response: Response,
): Promise<T | null> {
  if (!response.ok) {
    return null;
  }
  const body = (await response.json()) as CronoResponse<T>;
  return body?.data ?? null;
}
